import config from '../config/config'

/**
 * Format currency amount
 * @param {number} amount - Amount in euros
 * @param {string} currency - Currency code (default: EUR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale for formatting (default: es-ES)
 * @returns {string} Formatted date string
 */
// Safely parse various date input formats (ISO, MySQL, date-only, timestamp)
const parseDateSafe = (input) => {
  if (!input) return null

  // Date instance
  if (input instanceof Date) {
    return isNaN(input) ? null : input
  }

  // Numeric timestamp
  if (typeof input === 'number') {
    const d = new Date(input)
    return isNaN(d) ? null : d
  }

  // String parsing
  if (typeof input === 'string') {
    const s = input.trim()
    if (!s) return null

    // Pure digits -> timestamp
    if (/^\d+$/.test(s)) {
      const ts = Number(s)
      const d = new Date(ts)
      return isNaN(d) ? null : d
    }

    // ISO-like
    if (s.includes('T')) {
      const d = new Date(s)
      if (!isNaN(d)) return d
    }

    // MySQL DATETIME: YYYY-MM-DD HH:mm:ss
    const mysqlMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (mysqlMatch) {
      const [, y, m, d, hh, mm, ss = '0'] = mysqlMatch
      const dateObj = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss))
      return isNaN(dateObj) ? null : dateObj
    }

    // Date-only: YYYY-MM-DD
    const dateOnlyMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch
      const dateObj = new Date(Number(y), Number(m) - 1, Number(d))
      return isNaN(dateObj) ? null : dateObj
    }

    // Fallback: native parser
    const d = new Date(s)
    return isNaN(d) ? null : d
  }

  return null
}

export const formatDate = (date, locale = 'es-ES') => {
  const dateObj = parseDateSafe(date)
  if (!dateObj) return ''
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj)
}

/**
 * Format date and time for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale for formatting (default: es-ES)
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, locale = 'es-ES') => {
  const dateObj = parseDateSafe(date)
  if (!dateObj) return ''
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

/**
 * Calculate event pricing based on current date
 * @param {Object} event - Event object with date, early_fee, late_fee
 * @returns {Object} Pricing information
 */
export const calculateEventPricing = (event) => {
  const now = new Date()

  // Build event start from date and start_time when available
  let eventStart = null
  try {
    const dateStr = typeof event.date === 'string' ? event.date.split('T')[0] : event.date
    const timeStr = event.start_time || '00:00:00'
    eventStart = new Date(`${dateStr}T${timeStr}`)
    if (isNaN(eventStart)) eventStart = new Date(dateStr)
  } catch (_) {
    eventStart = new Date(event.date)
  }

  // Parse options for custom early bird limit
  let options = null
  try {
    options = event.options ? (typeof event.options === 'string' ? JSON.parse(event.options) : event.options) : null
  } catch (_) {
    options = null
  }

  let earlyBirdCutoff = null
  if (options && options.early_bird_limit && options.early_bird_limit.iso) {
    const parsed = new Date(options.early_bird_limit.iso)
    if (!isNaN(parsed.getTime())) {
      earlyBirdCutoff = parsed
    }
  }

  // Default: 3 days before event at 23:59 local time
  if (!earlyBirdCutoff) {
    earlyBirdCutoff = new Date(eventStart)
    earlyBirdCutoff.setDate(earlyBirdCutoff.getDate() - config.TICKETS.EARLY_BIRD_DAYS)
    earlyBirdCutoff.setHours(23, 59, 0, 0)
  }

  // Determine pricing
  let currentPrice = event.early_fee || 0
  let priceType = 'early'
  if (now >= earlyBirdCutoff) {
    currentPrice = event.late_fee || 0
    priceType = 'late'
  }

  return {
    currentPrice,
    priceType,
    earlyPrice: event.early_fee || 0,
    latePrice: event.late_fee || 0,
    earlyBirdCutoff,
    isEarlyBird: now < earlyBirdCutoff,
  }
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate verification code format
 * @param {string} code - Code to validate
 * @returns {boolean} True if code format is valid
 */
export const validateVerificationCode = (code) => {
  // Assuming 6-digit numeric code
  const codeRegex = /^\d{6}$/
  return codeRegex.test(code)
}

/**
 * Generate a random verification code
 * @param {number} length - Length of the code (default: 6)
 * @returns {string} Random numeric code
 */
export const generateVerificationCode = (length = 6) => {
  let code = ''
  for (let i = 0; i < length; i++) {
    code += Math.floor(Math.random() * 10).toString()
  }
  return code
}

/**
 * Calculate total order amount
 * @param {number} ticketPrice - Price per ticket
 * @param {number} quantity - Number of tickets
 * @returns {number} Total amount
 */
export const calculateOrderTotal = (ticketPrice, quantity) => {
  return ticketPrice * quantity
}

/**
 * Check if event is still available for purchase
 * @param {Object} event - Event object
 * @returns {boolean} True if event is available
 */
export const isEventAvailable = (event) => {
  // If event has no date, consider it unavailable
  if (!event.date) {
    return false
  }
  
  const now = new Date()
  
  // Combine date and start_time for accurate comparison
  let eventDate
  try {
    const dateStr = typeof event.date === 'string' ? event.date.split('T')[0] : event.date.toISOString().split('T')[0]
    const timeStr = event.start_time || '23:59:59' // Default to end of day if no time specified
    eventDate = new Date(`${dateStr}T${timeStr}`)
    
    // Fallback if invalid date created
    if (isNaN(eventDate.getTime())) {
      eventDate = new Date(event.date)
    }
  } catch (e) {
    eventDate = new Date(event.date)
  }
  
  // Check if the date is valid
  if (isNaN(eventDate.getTime())) {
    return false
  }

  // Add capacity check
  // If remainingTickets is explicitly null/undefined, we assume no capacity limit or unknown
  // But if it IS defined (number), we must check it.
  // Note: 0 is falsy, so be careful with checks.
  let hasCapacity = true
  if (event.remainingTickets !== undefined && event.remainingTickets !== null) {
    hasCapacity = event.remainingTickets > 0
  }
  
  return event.open && eventDate > now && !event.isSoldOut && hasCapacity
}

/**
 * Get time remaining until event
 * @param {string|Date} eventDate - Event date
 * @returns {Object} Time remaining object
 */
export const getTimeUntilEvent = (eventDate) => {
  const now = new Date()
  const event = new Date(eventDate)
  const diff = event - now

  if (diff <= 0) {
    return { expired: true }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  return {
    expired: false,
    days,
    hours,
    minutes,
    totalMinutes: Math.floor(diff / (1000 * 60)),
  }
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Email verification session management
 * These functions handle storing and retrieving email verification status
 * within the browser session to avoid re-verification on navigation
 */

const EMAIL_VERIFICATION_KEY = 'emailVerificationSession'
const VERIFICATION_EXPIRY_MINUTES = 10

/**
 * Store email verification status in session
 * @param {string} email - Verified email address
 * @param {string} eventId - Event ID for context
 */
export const storeEmailVerification = (email, eventId) => {
  try {
    const verificationData = {
      email: email.toLowerCase().trim(),
      eventId,
      timestamp: Date.now(),
      expiresAt: Date.now() + (VERIFICATION_EXPIRY_MINUTES * 60 * 1000)
    }
    
    sessionStorage.setItem(EMAIL_VERIFICATION_KEY, JSON.stringify(verificationData))
  } catch (error) {
    console.warn('Failed to store email verification:', error)
  }
}

/**
 * Check if email is already verified in current session
 * @param {string} email - Email to check
 * @param {string} eventId - Event ID for context
 * @returns {boolean} True if email is verified and not expired
 */
export const isEmailVerifiedInSession = (email, eventId) => {
  try {
    const stored = sessionStorage.getItem(EMAIL_VERIFICATION_KEY)
    if (!stored) return false
    
    const verificationData = JSON.parse(stored)
    const now = Date.now()
    
    // Check if verification has expired
    if (now > verificationData.expiresAt) {
      clearEmailVerification()
      return false
    }
    
    // Check if email and event match
    return verificationData.email === email.toLowerCase().trim() && 
           verificationData.eventId === eventId
  } catch (error) {
    console.warn('Failed to check email verification:', error)
    return false
  }
}

/**
 * Clear email verification from session
 * Called when email changes or verification expires
 */
export const clearEmailVerification = () => {
  try {
    sessionStorage.removeItem(EMAIL_VERIFICATION_KEY)
  } catch (error) {
    console.warn('Failed to clear email verification:', error)
  }
}

/**
 * Get stored verification data (for debugging/info purposes)
 * @returns {Object|null} Verification data or null if not found
 */
export const getStoredVerificationData = () => {
  try {
    const stored = sessionStorage.getItem(EMAIL_VERIFICATION_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.warn('Failed to get verification data:', error)
    return null
  }
}

/**
 * Refresh verification expiry (resets inactivity timeout)
 * @param {string} email - Email to refresh
 * @param {string} eventId - Event ID for context
 */
export const refreshEmailVerificationExpiry = (email, eventId) => {
  try {
    const stored = sessionStorage.getItem(EMAIL_VERIFICATION_KEY)
    if (!stored) return
    const data = JSON.parse(stored)
    if (data.email !== email.toLowerCase().trim() || data.eventId !== eventId) return
    data.expiresAt = Date.now() + (VERIFICATION_EXPIRY_MINUTES * 60 * 1000)
    data.timestamp = Date.now()
    sessionStorage.setItem(EMAIL_VERIFICATION_KEY, JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to refresh verification expiry:', error)
  }
}
